import React, { useState } from "react";
import { Box, Flex } from "theme-ui";
import { useNavigate } from "react-router-dom";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { createLead, FoundContact } from "api/Leads";

/**
 * Results of "Find contacts" on a lead (2026-10-15). The lead's own contact
 * was already completed server-side (blanks only); this lists the other
 * people at the organization, deciders first. Each can be added as a
 * contact - a lead for that person at the same organization (cloneOf) -
 * or, when this lead has no contact yet, made its main contact.
 */
const ROLE_NOTE: Record<number, string> = { 1: "Decision role", 2: "Decision role", 3: "Manager", 4: "Executive" };

export default function FindContactsPanel({
  lead,
  result,
  onMakeMain,
  onClose,
  onAdded,
}: {
  lead: any;
  result: { filled: string[]; people: FoundContact[]; steps: any[]; costUsd: number };
  onMakeMain: (c: FoundContact) => Promise<void>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [added, setAdded] = useState<Record<string, string>>({});
  const [working, setWorking] = useState<string | null>(null);
  const hasMain = !!(lead?.contactName ?? "").trim();

  const addAsContact = async (c: FoundContact) => {
    setWorking(c.name);
    const res: any = await createLead({
      cloneOf: lead.id,
      name: lead.name,
      website: lead.website ?? null,
      address: lead.address ?? null,
      category: lead.category ?? null,
      leadSource: lead.leadSource ?? null,
      leadOwnerId: lead.leadOwner?.id ?? lead.leadOwnerId ?? null,
      contactName: c.name,
      contactTitle: c.title ?? null,
      contactEmail: c.email ?? null,
      phone: c.phone ?? null,
    } as any);
    setWorking(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    setAdded((a) => ({ ...a, [c.name]: res.id }));
    toast.add(`${c.name} added as a contact at ${lead.name}.`, "var(--theme-ui-colors-success)");
    onAdded();
  };

  const people = result.people ?? [];
  return (
    <Box sx={{ marginTop: "12px", border: "1px solid", borderColor: "inputOutline", borderRadius: "8px", background: "#FFFFFF" }}>
      <Flex sx={{ padding: "12px 16px", borderBottom: "1px solid", borderColor: "inputOutline", alignItems: "center", gap: "8px" }}>
        <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, flex: 1 }}>
          {people.length ? `${people.length} ${people.length === 1 ? "person" : "people"} at ${lead.name}` : `No one else found at ${lead.name}`}
        </Box>
        <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Poppins", fontSize: 12, color: "#185FA5" }}>Close</button>
      </Flex>
      {result.filled?.length > 0 && (
        <Box sx={{ padding: "8px 16px", fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, borderBottom: "1px solid", borderColor: "inputOutline" }}>
          Filled on this lead: {result.filled.join(", ")}
        </Box>
      )}
      {people.map((c) => {
        const existing = c.existingLeadId || added[c.name];
        return (
          <Flex key={`${c.name}-${c.email ?? ""}`} sx={{ padding: "10px 16px", gap: "12px", alignItems: "flex-start", borderBottom: "1px solid", borderColor: "inputOutline", "&:last-of-type": { borderBottom: "none" } }}>
            <Box sx={{ flex: 1, minWidth: 0, fontFamily: "Poppins", fontSize: "13px", lineHeight: 1.5 }}>
              <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <Box sx={{ fontWeight: 600 }}>{c.name}</Box>
                {ROLE_NOTE[c.roleRank] && (
                  <Box sx={{ fontSize: "10px", fontWeight: 600, padding: "1px 6px", borderRadius: "10px", background: c.roleRank <= 2 ? "#E1F5EE" : "#F1EFE8", color: c.roleRank <= 2 ? "#0F6E56" : themevals.colors.dkgrey }}>
                    {ROLE_NOTE[c.roleRank]}
                  </Box>
                )}
              </Flex>
              {c.title && <Box sx={{ color: themevals.colors.dkgrey, fontSize: "12px" }}>{c.title}</Box>}
              {c.email && (
                <Box>
                  <a href={`mailto:${c.email}`} style={{ color: "#185FA5" }}>{c.email}</a>
                  <span style={{ fontSize: 11, color: c.emailVerified ? "#0F6E56" : themevals.colors.dkgrey }}>{c.emailVerified ? " · verified" : " · unverified"}</span>
                </Box>
              )}
              {c.phone && <Box>{c.phone}</Box>}
              {c.sourceUrl && /^https?:/.test(c.sourceUrl) && (
                <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  Source: <a href={c.sourceUrl} target="_blank" rel="noreferrer" style={{ color: themevals.colors.dkgrey }}>{c.sourceUrl.replace(/^https?:\/\//, "").slice(0, 60)}</a>
                </Box>
              )}
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              {existing ? (
                <button type="button" onClick={() => navigate(`/leads/${existing}`)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Poppins", fontSize: 12, color: "#185FA5" }}>
                  Open lead
                </button>
              ) : !hasMain ? (
                <BasicButton type="button" keepText disabled={!!working} $submitting={working === c.name} onClick={async () => { setWorking(c.name); await onMakeMain(c); setWorking(null); }}>
                  Make main contact
                </BasicButton>
              ) : (
                <BasicButton type="button" outline scheme="secondary" keepText disabled={!!working} $submitting={working === c.name} onClick={() => addAsContact(c)}>
                  Add as contact
                </BasicButton>
              )}
            </Box>
          </Flex>
        );
      })}
      <Box sx={{ padding: "8px 16px", fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey }}>
        {(result.steps ?? []).filter((s: any) => s.ran).map((s: any) => `${s.step}: ${s.detail}`).join(" · ") || "No sources ran."}
        {result.costUsd > 0 ? ` · research ~$${result.costUsd.toFixed(2)}` : ""}
      </Box>
    </Box>
  );
}
